// src/pages/AccountingPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatRut, formatCurrency } from '../utils/formatters';
import { accountingService } from '../services/accountingService';
import emailjs from 'emailjs-com'; 
import { EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, EMAILJS_PUBLIC_KEY, YOUR_EMAIL } from '../config/firebase';
import { 
    BanknotesIcon, DocumentTextIcon, PlusCircleIcon, TrashIcon, XCircleIcon,
    ReceiptPercentIcon, PencilSquareIcon, EnvelopeIcon, PaperAirplaneIcon,
    PaperClipIcon, EyeIcon
} from '@heroicons/react/24/outline';

// =============================================================================
// 1. COMPONENTE MODAL DE RECORDATORIO DE COBRANZA
// =============================================================================
const EmailReminderModal = ({ record, parameters, onClose, setNotification }) => {
    const { user } = useAuth();
    const [recipient, setRecipient] = useState('');
    const [cc, setCc] = useState('');
    const [sending, setSending] = useState(false);
    const [messageBody, setMessageBody] = useState('');

    useEffect(() => {
        const fetchClientEmail = async () => {
            if (record.clientId) {
                const data = await accountingService.getClientPaymentData(record.clientId);
                setRecipient(data.email);
            }
        };
        fetchClientEmail();
    }, [record]);

    useEffect(() => {
        const generateMessage = () => {
            let msg = `Estimado cliente,\n\n`;
            msg += `Le recordamos que mantiene un saldo pendiente de ${formatCurrency(record.balance)} asociado al servicio Folio: ${record.id}.\n\n`;
            
            if (record.invoices && record.invoices.length > 0) {
                msg += `Documentos pendientes:\n`;
                record.invoices.forEach(inv => {
                    msg += `- Doc N° ${inv.number} (${formatCurrency(inv.amount)})\n`;
                });
                msg += `\n`;
            } else if (record.invoiceNumber) {
                msg += `Documento Ref: ${record.invoiceNumber}\n\n`;
            }

            msg += `Agradecemos regularizar a la brevedad.\n\nAtentamente,\nBANT Ingeniería`;
            setMessageBody(msg);
        };
        generateMessage();
    }, [record]);

    const handleSendEmail = async (e) => {
        e.preventDefault();
        setSending(true);

        const bankDetails = `
-------------------------------------
DATOS DE TRANSFERENCIA:
Banco: ${parameters.bankName || ''}
Tipo Cta: ${parameters.bankAccountType || ''}
N° Cuenta: ${parameters.bankAccount || ''}
RUT: ${parameters.bankRut || ''}
Nombre: ${parameters.bankBeneficiaryName || ''}
Email: ${parameters.bankEmail || ''}
-------------------------------------
        `;

        const finalMessage = `${messageBody}\n\n${bankDetails}`;

        let fechaServicio = 'N/A';
        if (record.visitDate) {
            if (record.visitDate.seconds) fechaServicio = new Date(record.visitDate.seconds * 1000).toLocaleDateString('es-CL');
            else if (record.visitDate instanceof Date) fechaServicio = record.visitDate.toLocaleDateString('es-CL');
            else fechaServicio = new Date(record.visitDate).toLocaleDateString('es-CL');
        }

        const templateParams = {
            to_email: recipient,
            cc_email: cc,
            reply_to: user.email || YOUR_EMAIL,
            from_name: 'Cobranza - BANT Ingeniería',
            nombre_cliente: record.clientName,
            folio: record.id,
            fecha: fechaServicio,
            message: finalMessage, 
            my_html: finalMessage,
            notes: finalMessage,
            monto_pendiente: formatCurrency(record.balance)
        };

        const serviceId = parameters.emailJsServiceId || EMAILJS_SERVICE_ID;
        const templateId = parameters.emailJsTemplateId || EMAILJS_TEMPLATE_ID;
        const publicKey = parameters.emailJsPublicKey || EMAILJS_PUBLIC_KEY;

        try {
            await emailjs.send(serviceId, templateId, templateParams, publicKey);
            setNotification({ message: `Recordatorio enviado a ${recipient}.`, type: 'success' });
            onClose();
        } catch (error) {
            console.error('Error EmailJS:', error);
            setNotification({ message: 'Error al enviar. Verifique su plantilla de EmailJS.', type: 'error' });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/80 flex items-center justify-center z-[60] p-4 backdrop-blur-sm transition-colors">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg shadow-2xl border border-blue-100 dark:border-blue-900/50 transition-colors">
                <div className="flex justify-between items-center mb-4 border-b dark:border-gray-700 pb-2 transition-colors">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center transition-colors">
                        <EnvelopeIcon className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-500"/> 
                        Enviar Recordatorio de Pago
                    </h2>
                    <button onClick={onClose}><XCircleIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"/></button>
                </div>
                <form onSubmit={handleSendEmail} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1 transition-colors">Para</label>
                            <input type="email" value={recipient} onChange={e => setRecipient(e.target.value)} className="w-full border dark:border-gray-600 rounded-lg p-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-500 outline-none transition-colors" required />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1 transition-colors">CC</label>
                            <input type="email" value={cc} onChange={e => setCc(e.target.value)} className="w-full border dark:border-gray-600 rounded-lg p-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-500 outline-none transition-colors" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1 transition-colors">Mensaje (Editable)</label>
                        <textarea rows="6" value={messageBody} onChange={e => setMessageBody(e.target.value)} className="w-full border dark:border-gray-600 rounded-lg p-2 text-sm font-sans bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 outline-none transition-colors" required />
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-2 rounded border dark:border-gray-600 transition-colors">
                        <p><strong>Nota:</strong> Los datos bancarios se adjuntarán automáticamente al final del correo.</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">Cancelar</button>
                        <button type="submit" disabled={sending} className="px-4 py-2 bg-blue-600 dark:bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 dark:hover:bg-blue-700 flex items-center disabled:opacity-50 transition-colors">
                            {sending ? 'Enviando...' : <><PaperAirplaneIcon className="w-4 h-4 mr-2"/> Enviar Aviso</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// =============================================================================
// 2. COMPONENTE MODAL PRINCIPAL (InvoiceModal - GESTIÓN)
// =============================================================================
const InvoiceModal = ({ record, onClose, onUpdate, setNotification }) => {
    const [totalAmount, setTotalAmount] = useState(record.totalAmount || 0);
    const [activeTab, setActiveTab] = useState('facturas'); 
    const [isNoCharge, setIsNoCharge] = useState(record.status === 'Sin Cobro');
    const [loading, setLoading] = useState(false);
    const [params, setParams] = useState({ iva: 19, ppm: 1.5 });
    
    // --- ESTADOS FACTURAS ---
    const [invoicesList, setInvoicesList] = useState(() => {
        if (record.invoices && Array.isArray(record.invoices)) return record.invoices;
        if (record.invoiceNumber && record.invoiceNumber.trim() !== '') {
            return [{ id: Date.now(), number: record.invoiceNumber, date: new Date().toISOString().split('T')[0], amount: record.totalAmount, pdfUrl: '' }];
        }
        return [];
    });
    const [editingInvoiceId, setEditingInvoiceId] = useState(null); 
    const [newInvoice, setNewInvoice] = useState({ number: '', date: new Date().toISOString().split('T')[0], amount: '', pdfUrl: '' });

    // --- ESTADOS PAGOS ---
    const [paymentsList, setPaymentsList] = useState(record.payments || []);
    const [editingPaymentId, setEditingPaymentId] = useState(null); 
    const [newPayment, setNewPayment] = useState({ date: new Date().toISOString().split('T')[0], amount: '', method: 'Transferencia', relatedInvoice: '' });

    // --- ESTADO: GASTOS ---
    const [expensesList, setExpensesList] = useState([]);
    const [loadingExpenses, setLoadingExpenses] = useState(false);
    // Estados para edición de gastos eliminados porque los gastos se gestionan en FinancePage, 
    // aquí solo visualizamos los relacionados al proyecto.
    
    // Cargar parámetros del sistema al montar el modal
    useEffect(() => {
        const loadParams = async () => {
            const { parameters } = await accountingService.getAccountingData();
            if (parameters.iva) setParams({ iva: parameters.iva, ppm: parameters.ppm || 1.5 });
        };
        loadParams();
    }, []);

    const fetchExpenses = useCallback(async () => {
        setLoadingExpenses(true);
        try {
            const expensesData = await accountingService.getRelatedExpenses(record.id);
            setExpensesList(expensesData);
        } catch (error) {
            setNotification({ message: 'Error al cargar gastos.', type: 'error' });
        } finally { setLoadingExpenses(false); }
    }, [record.id, setNotification]);

    useEffect(() => { if (activeTab === 'gastos') fetchExpenses(); }, [activeTab, fetchExpenses]);

    // --- CÁLCULOS FINANCIEROS ---
    const totalPaid = paymentsList.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const pendingBalance = (parseFloat(totalAmount) || 0) - totalPaid;

    const suggestedStatus = useMemo(() => {
        if (isNoCharge) return 'Sin Cobro';
        if (pendingBalance <= 100) return 'Pagado';
        if (totalPaid > 0) return 'Abonado';
        if (invoicesList.length > 0) return 'Facturado (Pendiente Pago)';
        return 'Pendiente por Facturar';
    }, [totalPaid, pendingBalance, invoicesList, isNoCharge]);

    const financialData = useMemo(() => {
        const ivaRate = params.iva / 100;    
        const ppmRate = params.ppm / 100;    
        const brutoVenta = parseFloat(totalAmount) || 0;
        const netoVenta = Math.round(brutoVenta / (1 + ivaRate));
        const ivaDebito = brutoVenta - netoVenta;

        let gastoNetoContable = 0; 
        let ivaCredito = 0;        
        let gastoNoContable = 0;   
        let totalGastoBruto = 0;   

        expensesList.forEach(exp => {
            const montoBruto = parseFloat(exp.amount) || 0;
            totalGastoBruto += montoBruto;
            if (exp.docType === 'Factura' || exp.hasInvoice) {
                const neto = Math.round(montoBruto / (1 + ivaRate));
                const iva = montoBruto - neto;
                gastoNetoContable += neto;
                ivaCredito += iva;
            } else if (exp.docType === 'Boleta' || exp.docType === 'Sueldo') {
                gastoNetoContable += montoBruto;
            } else {
                gastoNoContable += montoBruto;
            }
        });

        const ivaPorPagar = Math.max(0, ivaDebito - ivaCredito);
        const utilidadContable = netoVenta - gastoNetoContable;
        const ppmPagar = Math.max(0, Math.round(utilidadContable * ppmRate));
        const utilidadReal = brutoVenta - totalGastoBruto - ivaPorPagar - ppmPagar;

        return { netoVenta, ivaDebito, gastoNetoContable, ivaCredito, gastoNoContable, totalGastoBruto, ivaPorPagar, utilidadContable, ppmPagar, utilidadReal };
    }, [totalAmount, expensesList, params]);

    // --- HANDLERS FACTURAS ---
    const handleCancelEditInvoice = () => {
        setEditingInvoiceId(null);
        setNewInvoice({ number: '', date: new Date().toISOString().split('T')[0], amount: '', pdfUrl: '' });
    };

    const handleInvoiceFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        try {
            const url = await accountingService.uploadFile(file, record.id, 'invoices');
            setNewInvoice(prev => ({ ...prev, pdfUrl: url }));
            setNotification({ message: 'Documento adjunto.', type: 'success' });
        } catch (err) { setNotification({ message: 'Error al subir documento.', type: 'error' }); }
        finally { setLoading(false); }
    };

    const handleAddOrUpdateInvoice = () => {
        if (!newInvoice.number || !newInvoice.amount) return alert("Faltan datos.");
        if (editingInvoiceId) {
            setInvoicesList(invoicesList.map(inv => inv.id === editingInvoiceId ? { ...inv, ...newInvoice, amount: parseFloat(newInvoice.amount) } : inv));
            handleCancelEditInvoice();
        } else {
            setInvoicesList([...invoicesList, { id: Date.now(), ...newInvoice, amount: parseFloat(newInvoice.amount) }]);
            setNewInvoice({ number: '', date: new Date().toISOString().split('T')[0], amount: '', pdfUrl: '' }); 
        }
    };

    const handlePrepareEditInvoice = (invoice) => {
        setNewInvoice(invoice);
        setEditingInvoiceId(invoice.id);
    };

    const handleDeleteInvoice = (id) => { if (window.confirm("¿Eliminar factura?")) setInvoicesList(invoicesList.filter(inv => inv.id !== id)); };
    
    // --- HANDLERS PAGOS ---
    const handleCancelEditPayment = () => {
        setEditingPaymentId(null);
        setNewPayment({ date: new Date().toISOString().split('T')[0], amount: '', method: 'Transferencia', relatedInvoice: '' });
    };

    const handleAddOrUpdatePayment = () => {
        if (!newPayment.amount) return alert("Monto requerido.");
        const paymentData = {
            amount: parseFloat(newPayment.amount),
            date: new Date(newPayment.date + "T12:00:00"),
            method: newPayment.method,
            relatedInvoice: newPayment.relatedInvoice || null
        };
        if (editingPaymentId) {
            setPaymentsList(paymentsList.map(p => p.id === editingPaymentId ? { ...p, ...paymentData } : p));
            handleCancelEditPayment();
        } else {
            setPaymentsList([...paymentsList, { id: Date.now(), ...paymentData }]);
            setNewPayment({ ...newPayment, amount: '', relatedInvoice: '' }); 
        }
    };
    const handlePrepareEditPayment = (payment) => {
        let dateStr = payment.date;
        if (payment.date?.seconds) dateStr = new Date(payment.date.seconds * 1000).toISOString().split('T')[0];
        else if (payment.date instanceof Date) dateStr = payment.date.toISOString().split('T')[0];
        setNewPayment({ date: dateStr, amount: payment.amount, method: payment.method, relatedInvoice: payment.relatedInvoice || '' });
        setEditingPaymentId(payment.id);
    };
    const handleDeletePayment = (id) => { if(window.confirm("¿Eliminar pago?")) setPaymentsList(paymentsList.filter(p => p.id !== id)); };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const updateData = {
                updatedAt: new Date(), // el servicio pondrá serverTimestamp pero por seguridad
                ...(isNoCharge 
                    ? { totalAmount: 0, paidAmount: 0, balance: 0, status: 'Sin Cobro', invoices: [], payments: [] } 
                    : { totalAmount: parseFloat(totalAmount), paidAmount: totalPaid, balance: pendingBalance, status: suggestedStatus, invoices: invoicesList, invoiceNumber: invoicesList.map(i => i.number).join(', '), payments: paymentsList }
                )
            };
            
            await accountingService.updateRecord(record.id, updateData);
            onUpdate(); 
            onClose(); 
            setNotification({ message: 'Guardado.', type: 'success' });
        } catch (e) { setNotification({ message: `Error: ${e.message}`, type: 'error' }); }
        finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 flex justify-center items-center z-50 p-4 backdrop-blur-sm transition-colors">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col transition-colors">
                
                <div className="flex justify-between items-center p-5 border-b dark:border-gray-800 bg-gray-50 dark:bg-gray-800 transition-colors">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Gestión Financiera: {record.id}</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors">{record.clientName}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"><XCircleIcon className="w-8 h-8" /></button>
                </div>

                <div className="p-4 bg-slate-50 dark:bg-gray-800/50 border-b border-slate-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-5 gap-4 text-sm transition-colors">
                    <div className="bg-white dark:bg-gray-800 p-3 rounded border border-blue-100 dark:border-blue-900/50 shadow-sm transition-colors">
                        <p className="text-xs font-bold text-blue-600 dark:text-blue-500 uppercase mb-2">1. Ingresos (Venta)</p>
                        <div className="flex justify-between items-center"><span className="text-gray-800 dark:text-gray-300">Bruto:</span> <input type="number" value={totalAmount} onChange={e => setTotalAmount(parseFloat(e.target.value)||0)} className="w-24 text-right font-bold border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 disabled:bg-gray-100 dark:disabled:bg-gray-800 rounded px-1 py-0.5" disabled={isNoCharge}/></div>
                        <div className="flex justify-between text-gray-500 dark:text-gray-400 text-xs mt-1"><span>Neto:</span> <span>{formatCurrency(financialData.netoVenta)}</span></div>
                        <div className="flex justify-between text-orange-500 dark:text-orange-400 text-xs"><span>IVA Débito:</span> <span>{formatCurrency(financialData.ivaDebito)}</span></div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-3 rounded border border-red-100 dark:border-red-900/50 shadow-sm transition-colors">
                        <p className="text-xs font-bold text-red-600 dark:text-red-500 uppercase mb-2">2. Costos y Gastos</p>
                        <div className="flex justify-between mt-1 text-gray-900 dark:text-gray-100"><span>Neto Contable:</span> <span className="font-bold">{formatCurrency(financialData.gastoNetoContable)}</span></div>
                        <div className="flex justify-between text-orange-500 dark:text-orange-400 text-xs"><span>IVA Crédito:</span> <span>{formatCurrency(financialData.ivaCredito)}</span></div>
                        <div className="flex justify-between text-gray-400 dark:text-gray-500 text-xs mt-1 pt-1 border-t dark:border-gray-700 border-dashed"><span>No Contable:</span> <span>{formatCurrency(financialData.gastoNoContable)}</span></div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-3 rounded border border-purple-100 dark:border-purple-900/50 shadow-sm transition-colors">
                        <p className="text-xs font-bold text-purple-600 dark:text-purple-500 uppercase mb-2">3. Utilidad Contable</p>
                        <div className="text-lg font-bold text-purple-700 dark:text-purple-400 text-center mt-1">{formatCurrency(financialData.utilidadContable)}</div>
                        <div className="text-center text-[10px] text-gray-400 dark:text-gray-500">Base para PPM</div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-3 rounded border border-orange-100 dark:border-orange-900/50 shadow-sm transition-colors">
                        <p className="text-xs font-bold text-orange-600 dark:text-orange-500 uppercase mb-2">4. Impuestos a Pagar</p>
                        <div className="flex justify-between mt-1 text-gray-900 dark:text-gray-100"><span>IVA Pagar:</span> <span className="font-bold text-red-600 dark:text-red-400">{formatCurrency(financialData.ivaPorPagar)}</span></div>
                        <div className="flex justify-between text-[10px] text-gray-400 dark:text-gray-500"><span>(Débito - Crédito)</span></div>
                        <div className="flex justify-between mt-1 pt-1 border-t dark:border-gray-700 border-dashed text-gray-900 dark:text-gray-100"><span>PPM ({params.ppm}%):</span> <span className="font-bold text-red-600 dark:text-red-400">{formatCurrency(financialData.ppmPagar)}</span></div>
                    </div>

                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded border border-green-200 dark:border-green-800/50 shadow-sm flex flex-col justify-center transition-colors">
                        <p className="text-xs font-bold text-green-700 dark:text-green-500 uppercase text-center">5. Utilidad Real</p>
                        <div className="text-2xl font-bold text-green-700 dark:text-green-400 text-center mt-1">{formatCurrency(financialData.utilidadReal)}</div>
                        <p className="text-xs text-center text-green-600 dark:text-green-500">"Bolsillo"</p>
                    </div>
                </div>

                <div className="px-6 py-2 bg-gray-100 dark:bg-gray-800 border-b dark:border-gray-700 flex justify-end transition-colors">
                    <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                        <input type="checkbox" checked={isNoCharge} onChange={e => setIsNoCharge(e.target.checked)} className="rounded text-orange-500 focus:ring-orange-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"/>
                        Marcar como "Sin Cobro"
                    </label>
                </div>

                {!isNoCharge && (
                    <div className="flex border-b dark:border-gray-700 transition-colors">
                        <button onClick={() => setActiveTab('facturas')} className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab==='facturas'?'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20':'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}><DocumentTextIcon className="w-5 h-5"/> Facturación</button>
                        <button onClick={() => setActiveTab('pagos')} className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab==='pagos'?'border-b-2 border-green-600 text-green-600 dark:text-green-400 dark:border-green-400 bg-green-50 dark:bg-green-900/20':'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}><BanknotesIcon className="w-5 h-5"/> Pagos y Abonos ({formatCurrency(pendingBalance)})</button>
                        <button onClick={() => setActiveTab('gastos')} className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab==='gastos'?'border-b-2 border-red-600 text-red-600 dark:text-red-400 dark:border-red-400 bg-red-50 dark:bg-red-900/20':'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}><ReceiptPercentIcon className="w-5 h-5"/> Gastos</button>
                    </div>
                )}

                {!isNoCharge && (
                    <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900/50 transition-colors">
                        {/* TAB 1: FACTURAS */}
                        {activeTab === 'facturas' && (
                            <div className="space-y-6">
                                <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-100 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400 transition-colors"><tr><th className="p-3">N° Doc</th><th className="p-3">Fecha</th><th className="p-3">Doc</th><th className="p-3 text-right">Monto</th><th className="p-3 text-center">Acciones</th></tr></thead>
                                        <tbody className="text-gray-800 dark:text-gray-200">{invoicesList.map(i => <tr key={i.id} className={`border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${editingInvoiceId === i.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}><td className="p-3">{i.number}</td><td className="p-3">{i.date}</td><td className="p-3">{i.pdfUrl && <a href={i.pdfUrl} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline flex items-center"><EyeIcon className="w-4 h-4 mr-1"/>Ver</a>}</td><td className="p-3 text-right">{formatCurrency(i.amount)}</td><td className="p-3 text-center space-x-2"><button onClick={()=>handlePrepareEditInvoice(i)} className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"><PencilSquareIcon className="w-4 h-4"/></button><button onClick={()=>handleDeleteInvoice(i.id)} className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"><TrashIcon className="w-4 h-4"/></button></td></tr>)}</tbody>
                                    </table>
                                </div>
                                <div className={`bg-white dark:bg-gray-800 p-4 rounded-lg border shadow-sm transition-colors ${editingInvoiceId ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-blue-200 dark:border-gray-700'}`}>
                                    <h4 className="text-xs font-bold text-blue-800 dark:text-blue-400 uppercase mb-3 flex justify-between items-center">{editingInvoiceId ? 'Editar Factura' : 'Agregar Nueva Factura'}{editingInvoiceId && <button onClick={handleCancelEditInvoice} className="text-[10px] bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancelar</button>}</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                                        <input type="text" placeholder="N° Documento" value={newInvoice.number} onChange={e=>setNewInvoice({...newInvoice, number: e.target.value})} className="border dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-blue-500 transition-colors"/>
                                        <input type="date" value={newInvoice.date} onChange={e=>setNewInvoice({...newInvoice, date: e.target.value})} className="border dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-blue-500 transition-colors"/>
                                        <input type="number" placeholder="Monto" value={newInvoice.amount} onChange={e=>setNewInvoice({...newInvoice, amount: e.target.value})} className="border dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-blue-500 transition-colors"/>
                                        <div className="relative">
                                            <input type="file" className="hidden" id="invoiceUpload" onChange={handleInvoiceFileChange}/>
                                            <label htmlFor="invoiceUpload" className={`flex items-center justify-center px-3 py-2 border rounded cursor-pointer text-sm transition-colors ${newInvoice.pdfUrl ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600'}`}>
                                                {newInvoice.pdfUrl ? <><EyeIcon className="w-4 h-4 mr-1"/>Listo</> : <><PaperClipIcon className="w-4 h-4 mr-1"/>Adjuntar</>}
                                            </label>
                                        </div>
                                    </div>
                                    <button onClick={handleAddOrUpdateInvoice} disabled={loading} className="mt-3 w-full bg-blue-600 text-white py-2 rounded text-sm font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2">{loading ? 'Subiendo...' : (editingInvoiceId ? 'Actualizar Factura' : <><PlusCircleIcon className="w-5 h-5"/> Agregar Documento</>)}</button>
                                </div>
                            </div>
                        )}

                        {/* TAB 2: PAGOS Y ABONOS */}
                        {activeTab === 'pagos' && (
                            <div className="space-y-6">
                                    <table className="w-full text-sm bg-white dark:bg-gray-800 rounded shadow transition-colors">
                                    <thead className="bg-gray-100 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400 transition-colors"><tr><th className="p-3">Fecha</th><th className="p-3">Medio</th><th className="p-3 text-right">Monto</th><th className="p-3 text-center">Acciones</th></tr></thead>
                                    <tbody className="text-gray-800 dark:text-gray-200">{paymentsList.map(p => <tr key={p.id} className={`border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${editingPaymentId === p.id ? 'bg-green-50 dark:bg-green-900/20' : ''}`}><td className="p-3">{(p.date?.seconds ? new Date(p.date.seconds * 1000) : new Date(p.date)).toLocaleDateString('es-CL')}</td><td className="p-3">{p.method}</td><td className="p-3 text-right text-green-600 dark:text-green-400 font-bold">{formatCurrency(p.amount)}</td><td className="p-3 text-center space-x-2"><button onClick={()=>handlePrepareEditPayment(p)} className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"><PencilSquareIcon className="w-4 h-4"/></button><button onClick={()=>handleDeletePayment(p.id)} className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"><TrashIcon className="w-4 h-4"/></button></td></tr>)}</tbody>
                                </table>
                                <div className={`bg-white dark:bg-gray-800 p-4 rounded-lg border shadow-sm transition-colors ${editingPaymentId ? 'border-green-400 dark:border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-green-200 dark:border-gray-700'}`}>
                                    <h4 className="text-xs font-bold text-green-800 dark:text-green-400 uppercase mb-3 flex justify-between items-center">{editingPaymentId ? 'Editar Pago' : 'Registrar Nuevo Pago'}{editingPaymentId && <button onClick={handleCancelEditPayment} className="text-[10px] bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancelar</button>}</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <input type="date" value={newPayment.date} onChange={e=>setNewPayment({...newPayment, date: e.target.value})} className="border dark:border-gray-600 rounded px-3 py-2 text-sm outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 transition-colors"/>
                                        <input type="number" placeholder="$ Abono" value={newPayment.amount} onChange={e=>setNewPayment({...newPayment, amount: e.target.value})} className="border dark:border-gray-600 rounded px-3 py-2 text-sm font-semibold outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 transition-colors"/>
                                        <select value={newPayment.method} onChange={e=>setNewPayment({...newPayment, method: e.target.value})} className="border dark:border-gray-600 rounded px-3 py-2 text-sm outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 transition-colors"><option>Transferencia</option><option>Efectivo</option><option>Cheque</option></select>
                                        <select value={newPayment.relatedInvoice} onChange={e => setNewPayment({...newPayment, relatedInvoice: e.target.value})} className="border dark:border-gray-600 rounded px-3 py-2 text-sm outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 transition-colors"><option value="">-- Abono General --</option>{invoicesList.map(inv => (<option key={inv.id} value={inv.id}>Fact. {inv.number}</option>))}</select>
                                    </div>
                                    <button onClick={handleAddOrUpdatePayment} className="mt-3 w-full bg-green-600 text-white py-2 rounded text-sm font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2">{editingPaymentId ? 'Actualizar Pago' : <><PlusCircleIcon className="w-5 h-5"/> Registrar Pago</>}</button>
                                </div>
                            </div>
                        )}

                        {/* TAB 3: GASTOS (VISUALIZACIÓN SOLO LECTURA EN ESTE CONTEXTO, EDICIÓN EN FINANCE) */}
                        {activeTab === 'gastos' && (
                            <div className="space-y-6">
                                {loadingExpenses ? <div className="text-center text-gray-500 dark:text-gray-400 py-4 transition-colors">Cargando gastos...</div> : (
                                    <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                                        <table className="w-full text-sm bg-white dark:bg-gray-800 transition-colors">
                                            <thead className="bg-gray-100 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400 transition-colors"><tr><th className="p-3">Fecha</th><th className="p-3">Descripción</th><th className="p-3">Doc</th><th className="p-3 text-right">Monto</th><th className="p-3 text-right">IVA Créd.</th></tr></thead>
                                            <tbody>
                                                {expensesList.map(exp => {
                                                    const isFactura = exp.docType === 'Factura' || exp.hasInvoice;
                                                    const ivaRate = params.iva / 100;
                                                    const ivaCredito = isFactura ? Math.round(exp.amount - (exp.amount / (1 + ivaRate))) : 0;
                                                    return (
                                                        <tr key={exp.id} className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                            <td className="p-3 text-gray-500 dark:text-gray-400 text-xs">{(exp.date?.seconds ? new Date(exp.date.seconds * 1000) : new Date(exp.date)).toLocaleDateString('es-CL')}</td>
                                                            <td className="p-3"><div className="font-medium text-gray-800 dark:text-gray-200">{exp.description}</div><div className="text-xs text-gray-500 dark:text-gray-400">{exp.category}</div></td>
                                                            <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isFactura ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>{exp.docType}</span></td>
                                                            <td className="p-3 text-right font-bold text-gray-700 dark:text-gray-200">{formatCurrency(exp.amount)}</td>
                                                            <td className="p-3 text-right text-xs text-orange-500 dark:text-orange-400">{ivaCredito > 0 ? formatCurrency(ivaCredito) : '-'}</td>
                                                        </tr>
                                                    );
                                                })}
                                                {expensesList.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-gray-400 dark:text-gray-500">No hay gastos asociados a este folio.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div className="p-4 bg-white dark:bg-gray-900 border-t dark:border-gray-800 flex justify-end gap-3 transition-colors">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">Cerrar</button>
                    <button onClick={handleSave} disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 transition">Guardar Cambios</button>
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// 3. COMPONENTE PRINCIPAL (AccountingPage)
// =============================================================================
const ManualInvoiceModal = ({ onClose, onUpdate, setNotification }) => {
    const [rut, setRut] = useState('');
    const [clientName, setClientName] = useState('');
    const [description, setDescription] = useState('');
    const [totalAmount, setTotalAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [origin, setOrigin] = useState('S/O');
    const [loading, setLoading] = useState(false);
    const [folio, setFolio] = useState('...');

    useEffect(() => {
        const generateManualId = async () => {
            try {
                const newId = await accountingService.generateManualId();
                setFolio(newId);
            } catch (error) { setFolio("Error"); }
        };
        generateManualId();
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        const cleanRut = rut.replace(/[.-]/g, '');
        const finalTotalAmount = parseFloat(totalAmount) || 0;

        if (!clientName.trim() || !description.trim() || finalTotalAmount <= 0 || folio === '...' || folio === 'Error') {
             setNotification({ message: 'Complete campos requeridos.', type: 'error' }); setLoading(false); return;
        }

        try {
            const recordData = {
                id: folio,
                clientName, 
                clientId: cleanRut, 
                visitDate: new Date(date),
                totalAmount: finalTotalAmount, 
                paidAmount: 0, 
                balance: finalTotalAmount,
                status: 'Pendiente por Facturar', 
                origin: origin, 
                description, 
                invoices: [], 
                payments: []
            };
            
            const clientData = { rut: cleanRut, name: clientName };

            await accountingService.createManualRecord(recordData, clientData);
            
            onUpdate(); onClose(); setNotification({ message: `Cobro ${folio} creado.`, type: 'success' });
        } catch (error) { setNotification({ message: 'Error al guardar.', type: 'error' }); } finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/80 flex items-center justify-center z-50 p-4 transition-colors">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg shadow-2xl transition-colors">
                <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-100 transition-colors">Nuevo Cobro Manual ({folio})</h2>
                <form onSubmit={handleSave} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <input type="text" placeholder="RUT" value={rut} onChange={e => setRut(formatRut(e.target.value))} className="border dark:border-gray-600 p-2 rounded w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-blue-500 transition-colors" required/>
                        <input type="text" placeholder="Nombre" value={clientName} onChange={e => setClientName(e.target.value)} className="border dark:border-gray-600 p-2 rounded w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-blue-500 transition-colors" required/>
                    </div>
                    <input type="text" placeholder="Descripción del cobro" value={description} onChange={e => setDescription(e.target.value)} className="border dark:border-gray-600 p-2 rounded w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-blue-500 transition-colors" required/>
                    <div className="grid grid-cols-2 gap-4">
                        <input type="number" placeholder="Monto Total" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} className="border dark:border-gray-600 p-2 rounded w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-blue-500 transition-colors" required/>
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="border dark:border-gray-600 p-2 rounded w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-blue-500 transition-colors" required/>
                    </div>
                    <select value={origin} onChange={e => setOrigin(e.target.value)} className="border dark:border-gray-600 p-2 rounded w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-blue-500 transition-colors">
                        <option value="S/O">Sin Origen</option><option value="Manual">Manual</option><option value="Otro">Otro</option>
                    </select>
                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={onClose} className="bg-gray-200 dark:bg-gray-700 px-4 py-2 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancelar</button>
                        <button type="submit" disabled={loading} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 shadow-lg disabled:opacity-50 transition-colors">Guardar</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const AccountingPage = ({ setPage, setNotification }) => {
    const { user } = useAuth(); 
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRecord, setSelectedRecord] = useState(null); 
    const [showManualInvoiceModal, setShowManualInvoiceModal] = useState(false); 
    
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailRecord, setEmailRecord] = useState(null);
    const [systemParameters, setSystemParameters] = useState({});

    const userPermissions = user?.permissions || {}; 
    const isSuperUser = user?.role === 'superuser';
    const isAdmin = user?.role === 'admin' || isSuperUser;

    const fetchRecords = useCallback(async () => {
        try {
            const { records: data, parameters } = await accountingService.getAccountingData();
            setRecords(data);
            setSystemParameters(parameters);
        } catch (error) {
            setNotification({ message: 'Error al cargar registros.', type: 'error' });
        } finally { if (loading) setLoading(false); }
    }, [setNotification, loading]);

    useEffect(() => { fetchRecords(); }, [fetchRecords]);

    const handleDeleteRecord = async (e, recordId) => {
        e.stopPropagation(); 
        if (!window.confirm(`¿Eliminar registro ${recordId}?`)) return;
        try {
            await accountingService.deleteRecord(recordId);
            setRecords(prev => prev.filter(r => r.id !== recordId));
            setNotification({ message: `Registro eliminado.`, type: 'success' });
        } catch (error) { setNotification({ message: 'Error al eliminar.', type: 'error' }); }
    };

    const handleRowClick = (record) => {
        setSelectedRecord(record);
    };

    const handleNavigate = (e, recordId, origin) => {
        e.stopPropagation();
        let effectiveOrigin = origin;
        if (!effectiveOrigin) {
            if (recordId.startsWith('COT-')) effectiveOrigin = 'Cotización';
            else if (recordId.startsWith('MAN-')) effectiveOrigin = 'S/O';
            else effectiveOrigin = 'Informe';
        }
        if (effectiveOrigin === 'Report') effectiveOrigin = 'Informe';

        if (effectiveOrigin === 'Informe') {
            setPage('viewReport', recordId);
        } else if (effectiveOrigin === 'Cotización') {
            const realQuoteId = recordId.replace(/^COT-/i, '').trim();
            setPage('viewQuote', realQuoteId);
        }
    };

    const handleOpenEmailModal = (e, record) => {
        e.stopPropagation();
        if (record.balance <= 0) {
            setNotification({ message: 'Este cliente no tiene deuda pendiente.', type: 'info' });
            return;
        }
        setEmailRecord(record);
        setShowEmailModal(true);
    };

    const getInvoiceDisplay = (record) => {
        let displayInvoices = [];
        if (record.invoices && Array.isArray(record.invoices)) displayInvoices = record.invoices.map(i => i.number);
        else if (record.invoiceNumber) displayInvoices = [record.invoiceNumber];

        if (displayInvoices.length === 0) return <span className="text-gray-400 dark:text-gray-500">-</span>;
        return (<div className="flex flex-wrap gap-1">{displayInvoices.map((num, idx) => (<span key={idx} className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded border border-blue-100 dark:border-blue-800/50 font-mono transition-colors">{num}</span>))}</div>);
    };

    const filteredRecords = records.filter(record =>
        (record.id && record.id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.clientName && record.clientName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors">
            {selectedRecord && !selectedRecord.isManual && <InvoiceModal record={selectedRecord} onClose={() => setSelectedRecord(null)} onUpdate={fetchRecords} setNotification={setNotification}/>}
            {showManualInvoiceModal && <ManualInvoiceModal onClose={() => setShowManualInvoiceModal(false)} onUpdate={fetchRecords} setNotification={setNotification}/>}
            {showEmailModal && emailRecord && (
                <EmailReminderModal 
                    record={emailRecord} 
                    parameters={systemParameters} 
                    onClose={() => setShowEmailModal(false)} 
                    setNotification={setNotification}
                />
            )}

            <div className="flex items-center mb-4">
                <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold mr-4 transition-colors">&larr; Volver</button>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Gestión Contable</h1>
            </div>
            
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                 {(isAdmin || userPermissions.accounting?.create) && (
                     <button onClick={() => setShowManualInvoiceModal(true)} className="w-full md:w-auto bg-green-500 text-white px-5 py-2 rounded-lg hover:bg-green-600 transition shadow-md font-semibold">+ Nuevo Cobro Manual</button>
                 )}
                <input type="text" placeholder="Buscar por Folio o Cliente..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="px-4 py-2 border dark:border-gray-700 rounded-lg w-full md:w-1/3 focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-colors"/>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 transition-colors mb-6">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 transition-colors">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Origen</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Cliente</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Fecha</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">N° Factura(s)</th> 
                                <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Total</th>
                                <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Estado</th>
                                <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Saldo</th>
                                <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors">Acción</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                            {loading ? <tr><td colSpan="8" className="text-center py-10 text-gray-500 dark:text-gray-400 transition-colors">Cargando registros...</td></tr> : (
                                filteredRecords.map(record => (
                                    <tr key={record.id} className="hover:bg-blue-50 dark:hover:bg-blue-900/20 transition duration-150">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {(record.origin !== 'S/O') ? (
                                                <button onClick={(e) => handleNavigate(e, record.id, record.origin)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline font-medium text-sm bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded transition-colors">{record.id}</button>
                                            ) : <span className="font-mono text-gray-500 dark:text-gray-400 text-sm transition-colors">{record.id}</span>}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 transition-colors">{record.clientName}</div>
                                            {record.description && <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px] transition-colors">{record.description}</div>}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 transition-colors">{record.visitDate?.seconds ? new Date(record.visitDate.seconds * 1000).toLocaleDateString('es-CL') : 'N/A'}</td>
                                        <td className="px-6 py-4">{getInvoiceDisplay(record)}</td>
                                        <td className="px-6 py-4 text-sm text-right font-semibold text-gray-700 dark:text-gray-200 transition-colors">{formatCurrency(record.totalAmount)}</td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full transition-colors ${
                                                record.status === 'Pagado' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-800/50' :
                                                record.status === 'Sin Cobro' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400 border border-orange-200 dark:border-orange-800/50' :
                                                record.status === 'Abonado' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800/50' :
                                                record.status === 'Facturado (Pendiente Pago)' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50' : 
                                                'bg-gray-100 dark:bg-gray-700/50 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
                                            }`}>{record.status || 'Pendiente'}</span>
                                        </td>
                                        <td className={`px-6 py-4 text-sm text-right font-bold transition-colors ${record.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'}`}>{formatCurrency(record.balance)}</td>
                                         <td className="px-6 py-4 text-center whitespace-nowrap text-sm font-medium flex justify-center items-center">
                                            {(isAdmin || userPermissions.accounting?.edit) && 
                                                <button onClick={() => handleRowClick(record)} className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mx-2 p-1 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition" title="Gestionar"><DocumentTextIcon className="w-5 h-5"/></button>
                                            }
                                            {(isAdmin || userPermissions.accounting?.edit) && record.balance > 0 && record.status !== 'Sin Cobro' && (
                                                <button onClick={(e) => handleOpenEmailModal(e, record)} className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 mx-2 p-1 hover:bg-green-50 dark:hover:bg-green-900/30 rounded transition" title="Enviar Recordatorio Pago">
                                                    <EnvelopeIcon className="w-5 h-5"/>
                                                </button>
                                            )}
                                             {(isAdmin || userPermissions.accounting?.delete) && 
                                                <button onClick={(e) => handleDeleteRecord(e, record.id)} className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 mx-2 p-1 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition" title="Eliminar"><TrashIcon className="w-5 h-5"/></button>
                                             }
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {!loading && filteredRecords.length === 0 && <div className="text-center py-10 text-gray-500 dark:text-gray-400 transition-colors">No se encontraron registros.</div>}
            </div>
        </div>
    );
};

export default AccountingPage;